import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const InvestmentChart = ({ 
  data = [], 
  rawData = [], 
  loading = false, 
  error = null,
  title = "Investment Trends",
  subtitle = "Annual spending by investment type"
}) => {
  const [plotBreakout, setPlotBreakout] = useState(false);

  const processPositionData = useMemo(() => {
    if (!rawData || rawData.length === 0) return {};

    const positionData = {};
    const positions = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

    positions.forEach(position => {
      const positionRecords = rawData.filter(record => record.Position === position);
      const yearInvestmentData = {};

      positionRecords.forEach(record => {
        const key = record.Year + '-' + record.Investment;
        if (!yearInvestmentData[key]) {
          yearInvestmentData[key] = {
            Year: record.Year,
            Investment: record.Investment,
            Spend: 0
          };
        }
        yearInvestmentData[key].Spend += record.Cost;
      });

      const yearlyData = {};
      Object.values(yearInvestmentData).forEach(item => {
        if (!yearlyData[item.Year]) {
          yearlyData[item.Year] = { Year: item.Year };
        }
        yearlyData[item.Year][item.Investment] = item.Spend;
      });

      positionData[position] = Object.values(yearlyData).sort((a, b) => a.Year - b.Year);
    });

    return positionData;
  }, [rawData]);

  const positionColors = {
    QB: { drafted: '#ef4444', keeper: '#f97316' },
    RB: { drafted: '#22c55e', keeper: '#3b82f6' },
    WR: { drafted: '#3b82f6', keeper: '#8b5cf6' },
    TE: { drafted: '#eab308', keeper: '#ef4444' },
    K: { drafted: '#a855f7', keeper: '#22c55e' },
    DEF: { drafted: '#6b7280', keeper: '#f59e0b' }
  };

  const formatCurrency = (value) => {
    return '$' + value;
  };

  // Enhanced tooltip formatter with percentages
  const formatTooltip = (value, name, props) => {
    if (!props || !props.payload) return ['$' + value, name];
    
    const data = props.payload;
    const drafted = data.Drafted || 0;
    const keeper = data.Keeper || 0;
    const total = drafted + keeper;
    
    if (total === 0) return ['$' + value, name];
    
    const percentage = ((value / total) * 100).toFixed(1);
    return [`$${value} (${percentage}%)`, name];
  };

  // Custom tooltip component for better control
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const drafted = data.Drafted || 0;
      const keeper = data.Keeper || 0;
      const total = drafted + keeper;
      
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => {
            const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0.0';
            return (
              <div key={index} className="flex items-center justify-between space-x-4">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className="text-sm text-gray-700">{entry.dataKey}</span>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900">${entry.value.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">{percentage}%</div>
                </div>
              </div>
            );
          })}
          <div className="border-t border-gray-100 mt-2 pt-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Total</span>
              <span className="font-semibold text-gray-900">${total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const PositionChart = ({ position, data, colors }) => (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-900 flex items-center">
          <span 
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: colors.drafted }}
          ></span>
          {position}
        </h4>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data}
            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis 
              dataKey="Year" 
              stroke="#6b7280"
              fontSize={10}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={10}
              tickFormatter={formatCurrency}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="Drafted" 
              stroke={colors.drafted}
              strokeWidth={2.5}
              dot={{ fill: colors.drafted, strokeWidth: 1, r: 3 }}
              activeDot={{ r: 5, fill: colors.drafted, stroke: '#fff', strokeWidth: 2 }}
              connectNulls={false}
            />
            <Line 
              type="monotone" 
              dataKey="Keeper" 
              stroke={colors.keeper}
              strokeWidth={2.5}
              dot={{ fill: colors.keeper, strokeWidth: 1, r: 3 }}
              activeDot={{ r: 5, fill: colors.keeper, stroke: '#fff', strokeWidth: 2 }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs pt-3 border-t border-gray-100">
        <div className="text-center">
          <div className="font-semibold text-gray-900">
            ${data.reduce((sum, d) => sum + (d.Drafted || 0), 0).toLocaleString()}
          </div>
          <div className="text-gray-600 flex items-center justify-center mt-1">
            <span 
              className="w-2 h-2 rounded-full mr-1"
              style={{ backgroundColor: colors.drafted }}
            ></span>
            Drafted
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {(() => {
              const totalDrafted = data.reduce((sum, d) => sum + (d.Drafted || 0), 0);
              const totalKeeper = data.reduce((sum, d) => sum + (d.Keeper || 0), 0);
              const total = totalDrafted + totalKeeper;
              const percentage = total > 0 ? ((totalDrafted / total) * 100).toFixed(1) : '0.0';
              return `${percentage}%`;
            })()}
          </div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-gray-900">
            ${data.reduce((sum, d) => sum + (d.Keeper || 0), 0).toLocaleString()}
          </div>
          <div className="text-gray-600 flex items-center justify-center mt-1">
            <span 
              className="w-2 h-2 rounded-full mr-1"
              style={{ backgroundColor: colors.keeper }}
            ></span>
            Keeper
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {(() => {
              const totalDrafted = data.reduce((sum, d) => sum + (d.Drafted || 0), 0);
              const totalKeeper = data.reduce((sum, d) => sum + (d.Keeper || 0), 0);
              const total = totalDrafted + totalKeeper;
              const percentage = total > 0 ? ((totalKeeper / total) * 100).toFixed(1) : '0.0';
              return `${percentage}%`;
            })()}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600 mt-1">{subtitle}</p>
          </div>
        </div>
        <div className="h-80 flex items-center justify-center">
          <div className="animate-pulse space-y-4 w-full">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600 mt-1">{subtitle}</p>
          </div>
        </div>
        <div className="h-80 flex items-center justify-center">
          <div className="text-center text-red-600">
            <p className="font-medium">Error loading chart data</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600 mt-1">{subtitle}</p>
          </div>
        </div>
        <div className="h-80 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="font-medium">No chart data available</p>
            <p className="text-sm mt-1">Try adjusting your filters to see trends</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600 mt-1">{subtitle}</p>
          <div className="mt-2 text-sm text-gray-500">
            {plotBreakout ? 'Faceted by position' : 'Trends across years'}
          </div>
        </div>
        <div className="mt-4 sm:mt-0">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={plotBreakout}
              onChange={(e) => setPlotBreakout(e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-gray-700">Break out by position</span>
          </label>
        </div>
      </div>
      
      {plotBreakout ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.keys(positionColors).map(position => {
              const positionData = processPositionData[position] || [];
              const totalDrafted = positionData.reduce((sum, d) => sum + (d.Drafted || 0), 0);
              const totalKeeper = positionData.reduce((sum, d) => sum + (d.Keeper || 0), 0);
              const total = totalDrafted + totalKeeper;

              return (
                <PositionChart 
                  key={position}
                  position={position} 
                  data={positionData} 
                  colors={positionColors[position]} 
                />
              );
            })}
          </div>
        </div>
      ) : (
        <>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={data}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="Year" 
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#6b7280"
                  fontSize={12}
                  tickFormatter={formatCurrency}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="Drafted" 
                  stroke="#6366f1" 
                  strokeWidth={3}
                  dot={{ fill: '#6366f1', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: '#6366f1', stroke: '#fff', strokeWidth: 2 }}
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="Keeper" 
                  stroke="#f97316" 
                  strokeWidth={3}
                  dot={{ fill: '#f97316', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: '#f97316', stroke: '#fff', strokeWidth: 2 }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 flex items-center justify-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">Drafted Players</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">Keeper Players</span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <div className="text-lg font-bold text-indigo-600">
                ${data.reduce((sum, d) => sum + (d.Drafted || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 flex items-center justify-center space-x-1">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Total Drafted</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {(() => {
                  const totalDrafted = data.reduce((sum, d) => sum + (d.Drafted || 0), 0);
                  const totalKeeper = data.reduce((sum, d) => sum + (d.Keeper || 0), 0);
                  const total = totalDrafted + totalKeeper;
                  const percentage = total > 0 ? ((totalDrafted / total) * 100).toFixed(1) : '0.0';
                  return `${percentage}% of total`;
                })()}
              </div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-orange-600">
                ${data.reduce((sum, d) => sum + (d.Keeper || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 flex items-center justify-center space-x-1">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span>Total Keepers</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {(() => {
                  const totalDrafted = data.reduce((sum, d) => sum + (d.Drafted || 0), 0);
                  const totalKeeper = data.reduce((sum, d) => sum + (d.Keeper || 0), 0);
                  const total = totalDrafted + totalKeeper;
                  const percentage = total > 0 ? ((totalKeeper / total) * 100).toFixed(1) : '0.0';
                  return `${percentage}% of total`;
                })()}
              </div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-indigo-600">
                ${data.length > 0 ? Math.round((data.reduce((sum, d) => sum + (d.Drafted || 0), 0) / data.length)) : 0}
              </div>
              <div className="text-sm text-gray-600 flex items-center justify-center space-x-1">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Avg Drafted/Year</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Over {data.length} years
              </div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-orange-600">
                ${data.length > 0 ? Math.round((data.reduce((sum, d) => sum + (d.Keeper || 0), 0) / data.length)) : 0}
              </div>
              <div className="text-sm text-gray-600 flex items-center justify-center space-x-1">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span>Avg Keepers/Year</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Over {data.length} years
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InvestmentChart;